import { gsap } from "gsap";

/**
 * CanvasSequencePlayer
 * Plays a frame sequence (image array or URL function) on a <canvas> using GSAP.
 *
 * QUICK START
 * -----------
 *   // Option A — pass an array of URLs
 *   const player = new CanvasSequencePlayer({
 *     canvas: "#myCanvas",              // CSS selector or DOM element
 *     frames: ["frame_00.jpg", "frame_01.jpg", ...],
 *   });
 *
 *   // Option B — pass a function that returns a URL for frame index i
 *   const player = new CanvasSequencePlayer({
 *     canvas: "#myCanvas",
 *     frames: (i) => `frames/frame_${String(i).padStart(4, "0")}.jpg`,
 *     frameCount: 60,
 *   });
 *
 *   await player.init();   // always await before calling any playback method
 *
 * CONSTRUCTOR OPTIONS
 * -------------------
 *   canvas      {string|HTMLCanvasElement}  required  CSS selector or element
 *   frames      {string[]|function}         required  URL array or (i) => url
 *   frameCount  {number}                    required when frames is a function
 *   fit         {"contain"|"cover"|"stretch"}  default "contain"
 *   dpr         {number}                    default window.devicePixelRatio
 *   preload     {boolean}                   default true — load all frames up front
 *
 * PLAYBACK METHODS
 * ----------------
 *   // Play frames 0–59 once, over 2 seconds
 *   player.playRange(0, 59, { duration: 2 });
 *
 *   // Loop frames 0–59 forever
 *   player.playRange(0, 59, { duration: 2, loop: true });
 *
 *   // Loop N times (e.g. 3)
 *   player.playRange(0, 59, { duration: 2, loop: 3 });
 *
 *   // Ping-pong: 0→59→0→59… (forward + backward loop)
 *   player.playPingPong(0, 59, { duration: 2 });
 *
 *   // Jump to a specific frame instantly
 *   player.render(30);
 *
 *   // Reverse direction while playing
 *   player.reverse();
 *
 *   // Update range on the fly without restarting from frame 0
 *   player.setRange(10, 40);
 *
 *   // Stop and freeze on the current frame
 *   player.stop();
 *
 *   // Tear down (call when removing from DOM)
 *   player.destroy();
 *
 * playRange OPTIONS
 * -----------------
 *   duration    {number}   seconds, default 1
 *   loop        {boolean|number}  true = infinite, number = repeat count, default false
 *   ease        {string}   GSAP ease string, default "none"
 *   yoyo        {boolean}  reverse on alternate repeats, default false
 *   onUpdate    {function} called each frame with current frame index
 *   onComplete  {function} called when animation finishes
 */
export class CanvasSequencePlayer {
  constructor(cfg) {
    const {
      canvas,
      frames,
      frameCount,
      dpr = window.devicePixelRatio || 1,
      fit = "contain",
      preload = true,
    } = cfg;

    this._canvas =
      typeof canvas === "string" ? document.querySelector(canvas) : canvas;
    if (!this._canvas) throw new Error("Canvas not found.");

    this._ctx = this._canvas.getContext("2d");
    this._dpr = dpr;
    this._fit = fit;
    this._preload = preload;

    this._count = Array.isArray(frames) ? frames.length : Number(frameCount || 0);
    if (!this._count || this._count < 1) {
      throw new Error("frameCount must be provided (and > 0) when frames is a function.");
    }

    this._getSrc = Array.isArray(frames) ? (i) => frames[i] : (i) => frames(i);

    this.images = new Array(this._count);
    this.playhead = { frame: 0 };

    this._resizeHandler = () => {
      // Re-measure and rerender on resize
      this._setupCanvasDPR();
      void this.render(this.playhead.frame);
    };

    // ResizeObserver catches the "first real size" + any container-driven resizes
    this._ro = new ResizeObserver(() => this._resizeHandler());

    this._tween = null;
    this._ready = false;
    this._playOpts = null;
    this._rangeStart = 0;
    this._rangeEnd = 0;
  }

  /** Call once after construction */
  async init() {
    console.log("canvas sequence player init");

    // 1) wait for at least one paint/layout pass
    await new Promise(requestAnimationFrame);

    // 2) if canvas is in a hidden container (tabs/modals), wait until it has size
    await this._waitForNonZeroSize();

    // 3) now size correctly + observe future size changes
    this._setupCanvasDPR();
    window.addEventListener("resize", this._resizeHandler);
    this._ro.observe(this._canvas);

    // Load frames
    if (this._preload) {
      await Promise.all(
        Array.from({ length: this._count }, async (_, i) => {
          this.images[i] = await this._loadImage(this._getSrc(i));
        })
      );
    } else {
      this.images[0] = await this._loadImage(this._getSrc(0));
    }

    this._ready = true;

    // Render first frame
    await this.render(0);

    // One extra RAF render helps in some layouts where the size finalizes right after init
    await new Promise(requestAnimationFrame);
    await this.render(this.playhead.frame);

    return this;
  }

  destroy() {
    this.stop();
    window.removeEventListener("resize", this._resizeHandler);
    if (this._ro) this._ro.disconnect();
  }

  stop() {
    if (this._tween) this._tween.kill();
    this._tween = null;
    gsap.killTweensOf(this.playhead);
  }

  get frameCount() {
    return this._count;
  }

  async render(frameIndex) {
    if (!this._ready) return;

    const i = this._clampFrame(frameIndex);
    this.playhead.frame = i;

    const img = this._preload ? this.images[i] : await this._ensureLoaded(i);
    this._drawFit(img);
  }

  playRange(startFrame, endFrame, opts = {}) {
    if (!this._ready) throw new Error("Call await player.init() before playRange().");

    const {
      duration = 1,
      loop = false,
      ease = "none",
      yoyo = false,
      onUpdate,
      onComplete,
    } = opts;

    const s = this._clampFrame(startFrame);
    const e = this._clampFrame(endFrame);

    this._rangeStart = s;
    this._rangeEnd = e;
    this._playOpts = { duration, loop, ease, yoyo, onUpdate, onComplete };

    this.stop();
    this.playhead.frame = s;

    const repeat =
      loop === true ? -1 : Number.isFinite(loop) ? Math.max(-1, loop) : 0;

    this._tween = gsap.to(this.playhead, {
      frame: e,
      duration,
      ease,
      snap: "frame",
      repeat,
      yoyo,
      onUpdate: () => {
        void this.render(this.playhead.frame);
        if (onUpdate) onUpdate(this.playhead.frame);
      },
      onComplete,
    });

    return this._tween;
  }

  /**
   * Ping-pong loop: plays startFrame→endFrame→startFrame repeatedly.
   * Shorthand for playRange with yoyo:true + loop:true.
   */
  playPingPong(startFrame, endFrame, opts = {}) {
    return this.playRange(startFrame, endFrame, {
      loop: true,
      yoyo: true,
      ...opts,
    });
  }

  /**
   * Update the frame range while animation is running.
   * Restarts the tween from the current frame with new bounds,
   * preserving all other playback options.
   */
  setRange(startFrame, endFrame) {
    if (!this._ready) return;
    const opts = this._playOpts || {};
    const currentFrame = this.playhead.frame;

    const s = this._clampFrame(startFrame);
    const e = this._clampFrame(endFrame);

    this._rangeStart = s;
    this._rangeEnd = e;

    this.stop();
    // Resume from wherever the playhead currently is (clamped to new range)
    this.playhead.frame = Math.max(s, Math.min(e, currentFrame));

    const repeat =
      opts.loop === true ? -1 : Number.isFinite(opts.loop) ? Math.max(-1, opts.loop) : 0;

    this._tween = gsap.to(this.playhead, {
      frame: e,
      duration: opts.duration ?? 1,
      ease: opts.ease ?? "none",
      snap: "frame",
      repeat,
      yoyo: opts.yoyo ?? false,
      onUpdate: () => {
        void this.render(this.playhead.frame);
        if (opts.onUpdate) opts.onUpdate(this.playhead.frame);
      },
      onComplete: opts.onComplete,
    });

    return this._tween;
  }

  /**
   * Reverse playback direction of the current tween mid-flight.
   */
  reverse() {
    if (this._tween) this._tween.reverse();
  }

  /* ------------------ internal helpers ------------------ */

  async _waitForNonZeroSize(maxFrames = 120) {
    for (let i = 0; i < maxFrames; i++) {
      const r = this._canvas.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return true;
      await new Promise(requestAnimationFrame);
    }
    // Don't hard-fail; ResizeObserver will eventually fix when it becomes visible
   // console.warn("Canvas stayed 0x0 during init; will render when it becomes visible.");
    return false;
  }

  _setupCanvasDPR() {
    const rect = this._canvas.getBoundingClientRect();

    // If still 0, avoid resetting width/height to 1 constantly
    if (rect.width <= 0 || rect.height <= 0) return;

    const w = Math.max(1, Math.round(rect.width * this._dpr));
    const h = Math.max(1, Math.round(rect.height * this._dpr));

    // Avoid clearing the canvas by reassigning same size repeatedly
    if (this._canvas.width !== w) this._canvas.width = w;
    if (this._canvas.height !== h) this._canvas.height = h;
  }

  _clampFrame(n) {
    const i = Math.round(Number(n) || 0);
    return Math.max(0, Math.min(this._count - 1, i));
  }

  _loadImage(src) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = img.onerror = () => resolve(img);
      img.src = src;
    });
  }

  async _ensureLoaded(i) {
    if (this.images[i]) return this.images[i];
    this.images[i] = await this._loadImage(this._getSrc(i));
    return this.images[i];
  }

  _drawFit(img) {
    const ctx = this._ctx;
    const cw = this._canvas.width;
    const ch = this._canvas.height;

    if (!img || !img.width || !img.height) return;
    if (cw <= 0 || ch <= 0) return;

    ctx.clearRect(0, 0, cw, ch);

    if (this._fit === "stretch") {
      ctx.drawImage(img, 0, 0, cw, ch);
      return;
    }

    const ir = img.width / img.height;
    const cr = cw / ch;
    const cover = this._fit === "cover";

    let dw, dh, dx, dy;

    if ((ir > cr && !cover) || (ir <= cr && cover)) {
      dw = cw;
      dh = cw / ir;
      dx = 0;
      dy = (ch - dh) / 2;
    } else {
      dh = ch;
      dw = ch * ir;
      dy = 0;
      dx = (cw - dw) / 2;
    }

    ctx.drawImage(img, dx, dy, dw, dh);
  }
}